import React, { useCallback } from 'react'
import cn from 'classnames'
import styles from 'components/panels/panel-container.module.scss'
import { getCenterIndex, isPanelCenter } from 'state/imperativeApis/swiperApi'
import useStore from 'state/knovStore'
import { PanelState } from 'state/PanelState'
import useRemovePanel from 'refactor/hooks/useRemovePanel'
import WithTooltip from 'components/shared/WithTooltip'

interface RemoveButtonProps {
    panel: PanelState
    index: number
    containerRef?: React.RefObject<HTMLDivElement>
    liftAboveBottomControls?: boolean
}

export default React.memo(function RemoveButton({
    panel,
    index,
    containerRef,
    liftAboveBottomControls = false,
}: RemoveButtonProps) {
    const centerIndex = useStore(state => getCenterIndex())
    const numPanels = useStore(state => state.numPanels)
    const removePanel = useRemovePanel(panel.panelId)
    const shouldShow = numPanels === 3 || index !== centerIndex

    const handleRemove = useCallback(() => {
        // Before the removal: a desktop three-panel close takes the panel out at once.
        const panelToLeft = useStore.getState().panels.getPanelToLeft(panel.panelId)
        if (isPanelCenter(panel.panelId) && useStore.getState().panels?.state?.length < 4) {
            // Convert to empty instead of removing if we've reached the smallest amount of panels necessary.
            useStore.getState().panels.clearPanel(panel.panelId)
        } else {
            removePanel() // Let the hook handle the direction internally
        }

        useStore.getState().set(draft => {
            draft.selectedAnswers[panel.panelId] = null
            if (panelToLeft) draft.selectedAnswers[panelToLeft.panelId] = null
        })
    }, [panel.panelId])

    if (!shouldShow) return null

    return (
        <WithTooltip asChild tip="Close panel.">
            <div
                ref={containerRef}
                data-testid="panel-remove-button"
                data-panel-id={panel.panelId}
                data-panel-index={index}
                className={cn(
                    styles.floatingControlContainer,
                    liftAboveBottomControls && styles.centerThreePanelFloatingControl,
                )}
                onClick={handleRemove}
            >
                <div className={cn(styles.closeButton)}>
                    <div className={styles.closeButtonFace}>
                        <i className="fa fa-times" />
                    </div>
                </div>
            </div>
        </WithTooltip>
    )
})
